/**
 * The doctor list (docs/ux/registry.md): readiness for one report type at a
 * time, chosen in the filter bar and kept in the URL, as a chip with a word
 * and a MISSING column naming the items. Blocked first, then inactive last.
 * Under 768 px the table gives way to cards and the filters to a sheet.
 */

import type { ReportTypeKind } from '@beam/core/status';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { cn } from 'cn';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { DoctorListRow, ReadinessOutcome } from '@/app/types';
import { useIsPhone, useViewportWidth } from '@/app/viewport';
import { FilterBar } from '@/components/shell/FilterBar';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { MatterCardList } from '@/components/shell/MatterCard';
import { PageHeader } from '@/components/shell/PageHeader';
import { NewDoctorDialog, NewUserDialog } from '../intake/RegistryDialogs';
import { useDemoPrefill } from '../intake/useIntake';
import { QueueTable } from '@/components/shell/QueueTable';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { useShellWidth } from '@/components/shell/context';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { shortDate } from '@/lib/format';
import { RegistryPill } from './RegistryPill';
import { ReadinessChip } from './ReadinessChip';
import { DICTATION_WORD, TYPES, isType, longType, missingText, readinessSentence, shortType } from './describe';
import { useDoctors } from './useRegistry';

type Show = 'all' | 'blocked' | 'not_decided' | 'ready';
const SHOWS: Show[] = ['all', 'blocked', 'not_decided', 'ready'];
const SHOW_LABEL: Record<Show, string> = { all: 'All', blocked: 'Blocked', not_decided: 'Not decided', ready: 'Ready' };

const ORDER: Record<ReadinessOutcome, number> = { blocked: 0, not_decided: 1, ready: 2 };

function useListParams() {
  const [params, setParams] = useSearchParams();
  const type: ReportTypeKind = isType(params.get('type')) ? (params.get('type') as ReportTypeKind) : 'ime';
  const show: Show = (SHOWS as string[]).includes(params.get('show') ?? '') ? (params.get('show') as Show) : 'all';
  const q = params.get('q') ?? '';
  const update = (next: Partial<{ type: ReportTypeKind; show: Show; q: string }>) => {
    setParams((p) => {
      if (next.type) p.set('type', next.type);
      if (next.show !== undefined) { if (next.show === 'all') p.delete('show'); else p.set('show', next.show); }
      if (next.q !== undefined) { if (next.q) p.set('q', next.q); else p.delete('q'); }
      if (!p.get('type')) p.set('type', 'ime');
      return p;
    }, { replace: true });
  };
  return { type, show, q, update, params };
}

export function DoctorListPage() {
  useShellWidth('wide');
  const phone = useIsPhone();
  const width = useViewportWidth();
  const { type, show, q, update, params } = useListParams();
  const [newDoctor, setNewDoctor] = useState(false);
  const [newUser, setNewUser] = useState(false);
  const prefill = useDemoPrefill();
  const doctors = useDoctors();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<{ type: ReportTypeKind; show: Show; q: string }>({ type, show, q });
  // The type lives in the URL so the Report tab can deep-link; a bare /registry becomes ?type=ime.
  const hasType = isType(params.get('type'));
  useEffect(() => { if (!hasType) update({ type: 'ime' }); }, [hasType]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = doctors.data ?? [];
  const counts = useMemo(() => {
    const c = { ready: 0, not_decided: 0, blocked: 0, inactive: 0 };
    for (const r of rows) { c[r.readiness[type].outcome] += 1; if (!r.active) c.inactive += 1; }
    return c;
  }, [rows, type]);
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => show === 'all' || r.readiness[type].outcome === show)
      .filter((r) => !needle || r.name.toLowerCase().includes(needle) || r.specialty.toLowerCase().includes(needle))
      .sort((a, b) => Number(!a.active) - Number(!b.active) || ORDER[a.readiness[type].outcome] - ORDER[b.readiness[type].outcome] || a.name.localeCompare(b.name));
  }, [rows, show, q, type]);

  const columns = useMemo<ColumnDef<DoctorListRow, unknown>[]>(() => [
    { id: 'doctor', accessorKey: 'name', header: 'Doctor', meta: { priority: 1, width: 260 }, cell: ({ row }) => <span data-slot="doctor-name" className={cn('font-semibold', !row.original.active && 'text-beam-text-muted')}>{row.original.name}</span> },
    { id: 'specialty', accessorKey: 'specialty', header: 'Specialty', meta: { priority: 2, width: 200 }, cell: ({ row }) => <span className="block truncate" title={row.original.specialty}>{row.original.specialty}</span> },
    { id: 'active', accessorKey: 'active', header: 'Active', enableSorting: false, meta: { priority: 2, width: 90 }, cell: ({ row }) => <span data-slot="active-word">{row.original.active ? 'Active' : 'Inactive'}</span> },
    { id: 'readiness', accessorFn: (r) => ORDER[r.readiness[type].outcome], header: `Readiness (${shortType(type)})`, meta: { priority: 1, width: 150 }, cell: ({ row }) => <ReadinessChip outcome={row.original.readiness[type].outcome} size="sm" /> },
    {
      id: 'missing', header: 'Missing', enableSorting: false, meta: { priority: 1 },
      cell: ({ row }) => {
        const text = missingText(row.original.readiness[type]);
        return (
          <Tooltip>
            <TooltipTrigger asChild><span id={`missing-${row.original.slug}`} data-slot="missing" className="block truncate">{text}</span></TooltipTrigger>
            <TooltipContent className="max-w-sm">{readinessSentence(type, row.original.readiness[type])}</TooltipContent>
          </Tooltip>
        );
      },
    },
    { id: 'dictation', accessorKey: 'dictationMode', header: 'Dictation', enableSorting: false, meta: { priority: 3, width: 110 }, cell: ({ row }) => <span data-slot="dictation-cell">{DICTATION_WORD[row.original.dictationMode]}</span> },
    { id: 'lastChange', accessorKey: 'updatedAt', header: 'Last change', meta: { priority: 3, width: 150 }, cell: ({ row }) => <span className="tabular block truncate">{shortDate(row.original.updatedAt)}{row.original.lastChange ? `, ${row.original.lastChange.byName}` : ''}</span> },
    { id: 'open', header: '', enableSorting: false, meta: { priority: 1, width: 96, align: 'right' }, cell: ({ row }) => <Link to={`/registry/doctors/${row.original.slug}?type=${type}`} aria-label={`Open ${row.original.name}`} aria-describedby={`missing-${row.original.slug}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>Open</Link> },
  ], [type]);

  const hint = `For ${shortType(type)}: ${counts.ready} ready, ${counts.not_decided} not decided, ${counts.blocked} blocked, ${counts.inactive} inactive. Order: blocked first, then name.`;
  const active = (show !== 'all' ? 1 : 0) + (q ? 1 : 0);
  const typeSelect = (value: ReportTypeKind, onChange: (t: ReportTypeKind) => void, id: string) => (
    <Select value={value} onValueChange={(v) => onChange(v as ReportTypeKind)}>
      <SelectTrigger id={id} aria-label="Report type" className={cn('bg-beam-paper', phone ? 'h-11 w-full' : 'h-9 min-w-44')}><SelectValue>{`Report type: ${width < 1300 && !phone ? shortType(value) : longType(value)}`}</SelectValue></SelectTrigger>
      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{longType(t)}</SelectItem>)}</SelectContent>
    </Select>
  );
  const empty = rows.length === 0
    ? <EmptyState title="No doctors in the registry yet." description="The seed creates the demo doctors; Beam's doctors arrive with the specialists directory." />
    : q && visible.length === 0
      ? <EmptyState title={`No doctor matches '${q}'.`} description="Check the spelling or clear the search." action={<Button variant="outline" onClick={() => update({ q: '' })}>Clear search</Button>} />
      : show === 'ready'
        ? <EmptyState title={`No doctor is ready for ${shortType(type)} yet.`} description="Readiness becomes decidable when ADR-0001 is accepted." action={<Button variant="outline" onClick={() => update({ show: 'all' })}>Clear filters</Button>} />
        : <EmptyState title="No doctors match these filters." action={<Button variant="outline" onClick={() => update({ show: 'all', q: '' })}>Clear filters</Button>} />;

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Doctors"
        description="Every doctor in the registry and whether each is ready to generate the chosen report type."
        tabs={<RegistryPill current="doctors" />}
        actions={
          <>
            {/* A doctor could not be created from any screen until now (docs/ux/intake.md). */}
            <Button className="h-11 md:h-9" onClick={() => setNewDoctor(true)}>New doctor</Button>
            {/* Demo builds only: the prefill query answers only where the demo tools are on. */}
            {prefill.data ? <Button variant="outline" className="h-11 md:h-9" onClick={() => setNewUser(true)}>New user</Button> : null}
          </>
        }
      />
      <NewDoctorDialog open={newDoctor} onOpenChange={setNewDoctor} />
      <NewUserDialog open={newUser} onOpenChange={setNewUser} />
      <form role="search" aria-label="Filter doctors" onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-1">
        <FilterBar
          desktop={(
            <>
              <Input type="search" value={q} onChange={(e) => update({ q: e.target.value })} placeholder="Search doctors" aria-label="Search doctors" className="h-9 w-56 bg-beam-paper" />
              {typeSelect(type, (t) => update({ type: t }), 'list-type')}
              <QueueTabs tabs={SHOWS.map((s) => ({ value: s, label: SHOW_LABEL[s], count: s === 'all' ? rows.length : counts[s] }))} value={show} onChange={(v) => update({ show: v as Show })} label="Show" />
            </>
          )}
          sheet={(
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-1"><Label htmlFor="sheet-q">Search doctors</Label><Input id="sheet-q" type="search" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} className="h-11 text-[16px]" /></div>
              <div className="flex flex-col gap-1"><Label htmlFor="sheet-type">Report type</Label>{typeSelect(draft.type, (t) => setDraft({ ...draft, type: t }), 'sheet-type')}</div>
              <div className="flex flex-col gap-1"><span className="text-sm font-medium">Show</span><QueueTabs tabs={SHOWS.map((s) => ({ value: s, label: SHOW_LABEL[s] }))} value={draft.show} onChange={(v) => setDraft({ ...draft, show: v as Show })} label="Show" /></div>
            </div>
          )}
          activeCount={active}
          hint={phone ? <span data-slot="list-hint">{counts.blocked} blocked for {shortType(type)}</span> : null}
          sheetOpen={sheetOpen}
          onSheetOpenChange={(o) => { if (o) setDraft({ type, show, q }); setSheetOpen(o); }}
          onApply={() => { update(draft); setSheetOpen(false); }}
          onClear={() => { setDraft({ type: 'ime', show: 'all', q: '' }); }}
        />
        {!phone ? <p data-slot="list-hint" className="text-xs text-beam-text-muted">{hint}</p> : null}
      </form>
      {doctors.isError && !doctors.data ? <ErrorPanel message="The doctor list could not be loaded." onRetry={() => void doctors.refetch()} /> : phone ? (
        <MatterCardList state={doctors.isPending ? 'loading' : 'ready'}>
          {visible.length === 0 && !doctors.isPending ? empty : visible.map((r) => <DoctorCard key={r.slug} row={r} type={type} />)}
        </MatterCardList>
      ) : (
        <QueueTable
          columns={columns}
          data={visible}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(r) => r.slug}
          rowHref={(r) => `/registry/doctors/${r.slug}?type=${type}`}
          caption={`Doctors in the registry and their readiness for ${longType(type)}`}
          state={doctors.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
      {!doctors.isPending && !doctors.isError ? <p className="text-xs text-beam-text-muted">Showing {visible.length} of {rows.length}</p> : null}
      <Legend>
        <LegendItem term={<ReadinessChip outcome="ready" size="sm" />}>every check met; generation reads this configuration</LegendItem>
        <LegendItem term={<ReadinessChip outcome="not_decided" size="sm" />}>every item Beam controls is set; a decision outside Beam is pending (the APIA pack, OPEN 76)</LegendItem>
        <LegendItem term={<ReadinessChip outcome="blocked" size="sm" />}>a check is missing; the column names it</LegendItem>
      </Legend>
    </div>
  );
}

function DoctorCard({ row, type }: { row: DoctorListRow; type: ReportTypeKind }) {
  const r = row.readiness[type];
  return (
    <article data-slot="doctor-card" data-slug={row.slug} aria-labelledby={`card-${row.slug}`} className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4">
      <h2 id={`card-${row.slug}`} className={cn('text-sm font-semibold', !row.active && 'text-beam-text-muted')}>{row.name}</h2>
      <p className="text-[13px] text-beam-text-muted">{row.specialty}{row.active ? '' : ' · Inactive'}</p>
      <div className="flex items-center gap-2"><ReadinessChip outcome={r.outcome} /> <span className="text-[13px]">for {shortType(type)}</span></div>
      <p className="text-[13px]">{r.outcome === 'not_decided' ? `Every check met; not decided: ${r.notDecided.join(', ')}` : r.missing.length > 0 ? `Missing: ${r.missing.join(', ')}` : 'Every check met'}</p>
      {r.outcome === 'blocked' && r.notDecided.length > 0 ? <p className="text-[13px] text-beam-text-muted">Not decided: {r.notDecided.join(', ')} (ADR-0001)</p> : null}
      <Link to={`/registry/doctors/${row.slug}?type=${type}`} className={cn(buttonVariants({ variant: 'outline' }), 'h-11 w-full')}>Open</Link>
    </article>
  );
}
